import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  UserCheck, 
  Home, 
  LogOut, 
  Settings,
  ChevronLeft,
  ChevronRight,
  Users,
  FileCheck,
  Vote
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';

const AlumniLayout = ({ children }) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('alumni_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [profileImage, setProfileImage] = useState(null);
  const [userName, setUserName] = useState(user?.name || 'Alumni');
  const [greeting, setGreeting] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    fetchProfile();
    updateGreeting();
    
    const timer = setInterval(() => {
      setCurrentDate(new Date());
      updateGreeting();
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);

  const updateGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) {
      setGreeting('Good Morning');
    } else if (hour < 17) {
      setGreeting('Good Afternoon');
    } else {
      setGreeting('Good Evening');
    }
  };

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('alumni_token');
      if (!token) return;
      
      const response = await api.get('/alumni/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        const profile = response.data.profile;
        if (profile.profile_image) {
          setProfileImage(profile.profile_image);
        }
        if (profile.full_name) {
          setUserName(profile.full_name);
          const updatedUser = { ...user, name: profile.full_name, profile_image: profile.profile_image };
          localStorage.setItem('alumni_user', JSON.stringify(updatedUser));
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('alumni_token');
    localStorage.removeItem('alumni_user');
    toast.success('Logged out successfully');
    navigate('/alumni/login');
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const menuItems = [
    { path: '/alumni/dashboard', icon: <Home size={20} />, label: 'Dashboard' },
    { path: '/alumni/directory', icon: <Users size={20} />, label: 'Directory' },
    { path: '/alumni/nomination', icon: <FileCheck size={20} />, label: 'Nominate' },
    { path: '/alumni/settings', icon: <Settings size={20} />, label: 'Settings' },
  ];

  const getProfileImageUrl = () => {
    if (profileImage) {
      if (profileImage.startsWith('http')) return profileImage;
      return `${profileImage}?t=${Date.now()}`;
    }
    return null;
  };

  const formatDate = () => {
    return currentDate.toLocaleDateString('en-KE', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = () => {
    return currentDate.toLocaleTimeString('en-KE', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <aside
        className={`bg-primary text-white transition-all duration-300 ${
          isCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-primary-light">
            {!isCollapsed ? (
              <div className="flex items-center gap-3">
                {getProfileImageUrl() ? (
                  <img 
                    src={getProfileImageUrl()}
                    alt={userName}
                    className="w-12 h-12 rounded-full object-cover border-2 border-white"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center"><svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>';
                    }}
                  />
                ) : (
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
                <div className="flex-1">
                  <p className="font-semibold text-sm truncate">{userName}</p>
                  <p className="text-xs text-secondary">Alumni Member</p>
                </div>
                <button onClick={toggleSidebar} className="p-1 rounded-lg hover:bg-primary-light">
                  <ChevronLeft size={18} />
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                {getProfileImageUrl() ? (
                  <img 
                    src={getProfileImageUrl()}
                    alt={userName}
                    className="w-10 h-10 rounded-full object-cover border-2 border-white mb-2"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mb-2"><svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg></div>';
                    }}
                  />
                ) : (
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mb-2">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
                <button onClick={toggleSidebar} className="p-1 rounded-lg hover:bg-primary-light mt-1">
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </div>

          <nav className="flex-1 py-6 overflow-y-auto">
            <ul className="space-y-2">
              {menuItems.map((item, index) => (
                <li key={index}>
                  <Link
                    to={item.path}
                    className={`flex items-center px-4 py-3 transition-colors hover:bg-primary-light ${
                      isCollapsed ? 'justify-center' : 'space-x-3'
                    }`}
                    title={isCollapsed ? item.label : ''}
                  >
                    <span className="text-xl">{item.icon}</span>
                    {!isCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="p-4 border-t border-primary-light">
            <button
              onClick={handleLogout}
              className={`flex items-center w-full px-4 py-3 rounded-lg hover:bg-red-600 transition-colors ${
                isCollapsed ? 'justify-center' : 'space-x-3'
              }`}
            >
              <LogOut size={20} />
              {!isCollapsed && <span className="text-sm font-medium">Logout</span>}
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm border-b">
          <div className="px-6 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">
                  {greeting}, {userName.split(' ')[0]}! 👋
                </h1>
                <p className="text-gray-500 text-sm mt-1">
                  Welcome to your alumni portal. Stay connected with your alma mater.
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">{formatDate()}</p>
                <p className="text-xs text-gray-400">{formatTime()}</p>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AlumniLayout;
